import { useMemo, useState } from "react";

import { AiSparkles } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { ContentBlur, EmptyState, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { getBaseWidgetRequestParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

import { FAKE_INSIGHTS, MIN_DAYS_FOR_INSIGHTS } from "./consts";
import { InsightCard } from "./InsightCard";
import { InsightCardLoader } from "./InsightCardLoader";
import { useCompetitiveInsights } from "./useCompetitiveInsights";

const CONTAINER_BACKGROUND = `linear-gradient(90deg, ${rgba(colorsPalettes.blue[300], 0.05)} 0.69%, ${rgba(colorsPalettes.mint[400], 0.05)} 99.61%)`;

export const CompetitiveInsightsWidget = ({
    campaign,
    campaignConfig,
    reportDuration,
    reportStatus,
}: ILLMReportWidget) => {
    const [isFetchTriggered, setFetchTrigger] = useState<boolean>(false);
    const reportDays = reportDuration ? reportDuration.to.endOf("day").diff(reportDuration.from, "days") + 1 : 0;
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign && isFetchTriggered;
    const apiParams = useMemo(
        () => ({
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
        }),
        [campaign, campaignConfig, reportDuration],
    );

    const {
        insights,
        isLoading: isLoadingData,
        isError,
    } = useCompetitiveInsights(apiParams, {
        enabled: canLoadData,
    });

    const hasData = !!insights?.length;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;
    const isBlurred = !isFetchTriggered && !hasData;
    const displayedInsights = isBlurred ? FAKE_INSIGHTS : insights;

    const fetchResults = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Competitive Insights/Reveal");
        setFetchTrigger(true);
    };

    if (reportDays < MIN_DAYS_FOR_INSIGHTS) {
        return null;
    }

    return (
        <ContentBlur
            enabled={isBlurred}
            button={
                <IconButton
                    data-automation="overview-report-insights-widget-reveal"
                    iconName="sparkles-ai-2"
                    isLoading={isLoadingData}
                    isDisabled={reportStatus !== ReportStatus.Ready}
                    onClick={fetchResults}
                >
                    {translate("llm.overview_report.insights.hook.button")}
                </IconButton>
            }
        >
            <Flex
                flexDirection="column"
                gap="8px"
                padding="12px"
                border={`1px solid ${rgba(colorsPalettes.blue[400], 0.2)}`}
                borderRadius="8px"
                background={CONTAINER_BACKGROUND}
                data-automation="overview-report-insights-widget"
            >
                <Flex
                    gap="4px"
                    alignItems="center"
                >
                    <AiSparkles size="xs" />
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate("llm.overview_report.insights.title")}
                    </Text>
                </Flex>
                {shouldShowLoading ? (
                    <Flex gap="12px">
                        {Array.from({ length: 3 }, (_, i) => (
                            <InsightCardLoader key={i} />
                        ))}
                    </Flex>
                ) : (
                    <Flex
                        justifyContent="center"
                        gap="12px"
                    >
                        {displayedInsights?.length ? (
                            displayedInsights.map((insight, index) => (
                                <InsightCard
                                    key={insight.type}
                                    type={insight.type}
                                    text={insight.text}
                                    index={index}
                                />
                            ))
                        ) : (
                            <EmptyState
                                title={translate(isError ? "common.error" : "common.noresults")}
                                subtitle={translate(isError ? "common.error.subtitle" : "common.noresults.subtitle")}
                            />
                        )}
                    </Flex>
                )}
            </Flex>
        </ContentBlur>
    );
};
